import {
  DashValidation,
  isBrokerLicenseDescriptionLengthValid,
} from 'dash-common';
import {
  Form,
  FormErrorTypes,
  Textarea,
  Modal,
  useFormState,
  useFormValidation,
  toast,
} from 'dash-ui';
import { ChangeEvent, FormEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetBrokerByIdDocument,
  BrokerLicenseStatusesEnum,
  useUpdateBrokerLicenseStatusMutation,
} from 'gql/generated.graphql';

enum FormFields {
  Reason = 'reason',
}

const FormFieldsList = [FormFields.Reason];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  brokerId: string;
};

export const DeActivateBrokerLicenseModal = ({
  isOpen,
  onClose,
  brokerId,
}: Props) => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const formRef = useRef<HTMLFormElement>(null);
  const [formErrors, , [setReasonError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [formState, [setReason], [formOnSubmit, formOnError, , , resetForm]] =
    useFormState(FormFieldsList);

  const [updateBrokerLicenseStatus, { loading }] =
    useUpdateBrokerLicenseStatusMutation({
      refetchQueries: [
        {
          query: GetBrokerByIdDocument,
          variables: {
            brokerId,
          },
        },
      ],
    });

  const validateName = (value: string) => {
    if (!value) {
      setReasonError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.reasonBlank'),
      });
      return false;
    }
    if (!isBrokerLicenseDescriptionLengthValid(value)) {
      setReasonError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.BrokerLicenseDescriptionLength.toString(),
        }),
      });
      return false;
    }
    setReasonError(null);
    return true;
  };

  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    validateName(e.target.value);
  };

  const onCloseModal = () => {
    resetForm();
    resetFormErrors();
    onClose();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();
    const { reason } = formState;

    if (!validateName(reason)) {
      return null;
    }

    updateBrokerLicenseStatus({
      variables: {
        brokerId,
        input: {
          reason,
          status: BrokerLicenseStatusesEnum.Inactive,
        },
      },
      onCompleted: () => {
        toast.success(
          t(`pages:brokersPage:modals:deactivateBrokerLicenseSuccess`)
        );
        onCloseModal();
      },
      onError: (error) => {
        formOnError(
          t(`common:serverMessages.${error.message}`) || error.message
        );
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCloseModal}
      title={t(`pages:brokersPage:modals:deactivateBrokerLicenseTitle`) || ''}
      cancelButton={{ title: t('common:labels.cancel'), onClick: onCloseModal }}
      submitButton={{
        title: t('common:labels.submit'),
        colorVariation: 'danger',
        isLoading: loading,
        disabled: loading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={2}>
          <Form.Label required>{t('forms:labels.reasonUnActive')}</Form.Label>
          <Textarea
            w="100%"
            placeholder={t('forms:placeholders.reasonUnActive') || ''}
            onChange={onTextChange}
            $errorType={formErrors[FormFields.Reason]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Reason]?.type}>
            {formErrors[FormFields.Reason]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default DeActivateBrokerLicenseModal;
